import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { format } from 'date-fns'

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF
  }
}

export interface ExportOptions {
  filename?: string
  format?: 'xlsx' | 'csv' | 'pdf'
  includeMetadata?: boolean
  includeTimestamp?: boolean
}

export class ExportUtils {
  /**
   * Export data to Excel with multiple sheets
   */
  static async exportToExcel(
    sheets: Array<{
      name: string
      data: any[]
      columns?: Array<{ header: string; key: string; width?: number }>
    }>,
    options: ExportOptions = {}
  ) {
    const {
      filename = 'export',
      includeMetadata = true,
      includeTimestamp = true
    } = options

    const wb = XLSX.utils.book_new()

    // Add metadata sheet if requested
    if (includeMetadata) {
      const metadata = [
        ['Generated Date', format(new Date(), 'MMMM d, yyyy h:mm a')],
        ['Generated By', 'Share Lattice'],
        ['Version', '1.0.0'],
        ['Sheets Included', sheets.map(s => s.name).join(', ')]
      ]
      const metadataWs = XLSX.utils.aoa_to_sheet(metadata)
      XLSX.utils.book_append_sheet(wb, metadataWs, 'Metadata')
    }

    // Add data sheets
    sheets.forEach(sheet => {
      let ws: XLSX.WorkSheet

      if (sheet.columns) {
        // Convert data to array format with headers
        const headers = sheet.columns.map(col => col.header)
        const rows = sheet.data.map(row =>
          sheet.columns!.map(col => row[col.key] ?? '')
        )
        ws = XLSX.utils.aoa_to_sheet([headers, ...rows])

        // Set column widths
        const colWidths = sheet.columns.map(col => ({ wch: col.width || 15 }))
        ws['!cols'] = colWidths
      } else {
        // Use json_to_sheet for simple data
        ws = XLSX.utils.json_to_sheet(sheet.data)
      }

      XLSX.utils.book_append_sheet(wb, ws, sheet.name)
    })

    // Generate filename with timestamp if requested
    const finalFilename = includeTimestamp
      ? `${filename}_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`
      : `${filename}.xlsx`

    // Write file
    XLSX.writeFile(wb, finalFilename)
  }

  /**
   * Export data to CSV
   */
  static exportToCSV(
    data: any[],
    columns: Array<{ header: string; key: string }>,
    options: ExportOptions = {}
  ) {
    const {
      filename = 'export',
      includeTimestamp = true
    } = options

    // Create CSV content
    const headers = columns.map(col => col.header).join(',')
    const rows = data.map(row =>
      columns.map(col => {
        const value = row[col.key] ?? ''
        // Escape values containing commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }).join(',')
    )

    const csv = [headers, ...rows].join('\n')

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    
    const finalFilename = includeTimestamp
      ? `${filename}_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`
      : `${filename}.csv`
    
    a.download = finalFilename
    a.click()
    window.URL.revokeObjectURL(url)
  }

  /**
   * Export data to PDF
   */
  static exportToPDF(
    title: string,
    sections: Array<{
      title?: string
      data: any[]
      columns: Array<{ header: string; key: string }>
      summary?: string
    }>,
    options: ExportOptions = {}
  ) {
    const {
      filename = 'export',
      includeTimestamp = true,
      includeMetadata = true
    } = options

    const doc = new jsPDF()
    let yPosition = 20

    // Add title
    doc.setFontSize(20)
    doc.text(title, 20, yPosition)
    yPosition += 15

    // Add metadata if requested
    if (includeMetadata) {
      doc.setFontSize(10)
      doc.setTextColor(100)
      doc.text(`Generated: ${format(new Date(), 'MMMM d, yyyy h:mm a')}`, 20, yPosition)
      yPosition += 10
      doc.setTextColor(0)
    }

    // Add sections
    sections.forEach((section, index) => {
      if (index > 0) {
        // Add new page if needed
        if (yPosition > 250) {
          doc.addPage()
          yPosition = 20
        }
      }

      // Section title
      if (section.title) {
        doc.setFontSize(14)
        doc.text(section.title, 20, yPosition)
        yPosition += 10
      }

      // Section summary
      if (section.summary) {
        doc.setFontSize(10)
        doc.setTextColor(100)
        const lines = doc.splitTextToSize(section.summary, 170)
        lines.forEach((line: string) => {
          doc.text(line, 20, yPosition)
          yPosition += 5
        })
        doc.setTextColor(0)
        yPosition += 5
      }

      // Table data
      const tableData = section.data.map(row =>
        section.columns.map(col => {
          const value = row[col.key]
          if (value === null || value === undefined) return ''
          if (typeof value === 'number') {
            // Format numbers
            return value.toLocaleString()
          }
          return String(value)
        })
      )

      doc.autoTable({
        head: [section.columns.map(col => col.header)],
        body: tableData,
        startY: yPosition,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202] },
        styles: { fontSize: 9 },
        margin: { left: 20, right: 20 }
      })

      // Update y position
      yPosition = (doc as any).lastAutoTable.finalY + 10
    })

    // Save PDF
    const finalFilename = includeTimestamp
      ? `${filename}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`
      : `${filename}.pdf`

    doc.save(finalFilename)
  }

  /**
   * Format currency values
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  /**
   * Format percentage values
   */
  static formatPercentage(value: number, decimals: number = 2): string {
    return `${(value * 100).toFixed(decimals)}%`
  }

  /**
   * Format number with commas
   */
  static formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(Math.round(value))
  }
}